import { SHIFT_TYPES_OPTIONS } from '@src/packages/shifts/constants';
import { SHIFT_FILTER_DEFAULTS } from '@src/packages/shifts/shiftsListFilterConfig';
import type { ShiftFiltersState } from '@src/packages/shifts/types';
import { normalizeDaysOfWeek } from '@src/packages/utils/dates/formatDaysOfWeek';

export const hasTextShiftFilters = ({
  searchTerm,
}: Pick<ShiftFiltersState, 'searchTerm'>): boolean => Boolean(searchTerm.trim());

export const hasElasticShiftFilters = ({
  searchTerm,
  shiftType,
  exceptional,
}: Pick<ShiftFiltersState, 'searchTerm' | 'shiftType' | 'exceptional'>): boolean =>
  hasTextShiftFilters({ searchTerm }) ||
  shiftType === SHIFT_TYPES_OPTIONS.EXCEPTIONAL ||
  typeof exceptional === 'boolean';

export const hasFirestoreShiftFilters = ({
  shiftType,
  selectedDays,
  status,
}: Pick<ShiftFiltersState, 'shiftType' | 'selectedDays' | 'status'>): boolean =>
  shiftType !== SHIFT_FILTER_DEFAULTS.SHIFT_TYPE ||
  status !== SHIFT_FILTER_DEFAULTS.STATUS ||
  selectedDays.length > 0;

export const hasAnyShiftListFilters = (filters: ShiftFiltersState): boolean =>
  hasElasticShiftFilters(filters) || hasFirestoreShiftFilters(filters);

export const hasPersistedBootstrapShiftFilters = (filters: ShiftFiltersState): boolean =>
  hasTextShiftFilters(filters) ||
  filters.shiftType !== SHIFT_FILTER_DEFAULTS.SHIFT_TYPE ||
  filters.selectedDays.length > 0 ||
  filters.status !== SHIFT_FILTER_DEFAULTS.STATUS ||
  typeof filters.exceptional === 'boolean';

const normalizeShiftSelectedDays = (selectedDays: ShiftFiltersState['selectedDays']): number[] =>
  normalizeDaysOfWeek(selectedDays);

export const areShiftListFiltersEqual = (
  left: ShiftFiltersState,
  right: ShiftFiltersState,
): boolean => {
  const leftDays = normalizeShiftSelectedDays(left.selectedDays);
  const rightDays = normalizeShiftSelectedDays(right.selectedDays);

  return (
    left.searchTerm.trim() === right.searchTerm.trim() &&
    left.shiftType === right.shiftType &&
    left.status === right.status &&
    left.exceptional === right.exceptional &&
    leftDays.length === rightDays.length &&
    leftDays.every((day, index) => day === rightDays[index])
  );
};

export const shouldUsePersistedListFiltersBootstrap = (
  debouncedFilters: ShiftFiltersState,
  persistedFilters: ShiftFiltersState,
  bootstrapComplete: boolean,
): boolean =>
  !bootstrapComplete &&
  hasPersistedBootstrapShiftFilters(persistedFilters) &&
  !areShiftListFiltersEqual(debouncedFilters, persistedFilters);

export const shouldCompletePersistedListFiltersBootstrap = (
  debouncedFilters: ShiftFiltersState,
  persistedFilters: ShiftFiltersState,
  currentFilters: ShiftFiltersState,
): boolean => {
  if (areShiftListFiltersEqual(debouncedFilters, persistedFilters)) {
    return true;
  }

  if (!hasPersistedBootstrapShiftFilters(currentFilters)) {
    if (
      !hasPersistedBootstrapShiftFilters(debouncedFilters) &&
      hasPersistedBootstrapShiftFilters(persistedFilters)
    ) {
      return false;
    }

    return true;
  }

  return false;
};
